import { useEffect } from "react";

type JsonLd = Record<string, unknown> | Array<Record<string, unknown>>;

export interface PageMetaOptions {
  title: string;
  description: string;
  keywords?: string;
  path: string;
  type?: string;
  image?: string;
  jsonLd?: JsonLd;
  robots?: string;
}

const DEFAULT_IMAGE =
  "https://readdy.ai/api/search-image?query=Stylized%20editorial%20abstract%20scene%20of%20a%20dramatic%20theatrical%20spotlight%20beam%20cutting%20through%20warm%20haze%20over%20a%20bold%20coral%20red%20stage%20backdrop%2C%20cinematic%20grain%2C%20soft%20glowing%20particles%2C%20minimal%20geometric%20shapes%2C%20warm%20cream%20and%20coral%20color%20palette%2C%20no%20text%2C%20magazine%20cover%20aesthetic&width=1200&height=630&seq=gossip-og-cover&orientation=landscape";

function upsertMeta(attr: "name" | "property", key: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertCanonical(href: string) {
  let el = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

export function usePageMeta(options: PageMetaOptions) {
  const {
    title,
    description,
    keywords,
    path,
    type = "website",
    image = DEFAULT_IMAGE,
    robots = "index,follow",
    jsonLd,
  } = options;

  const jsonLdString = jsonLd ? JSON.stringify(jsonLd) : "";

  useEffect(() => {
    const rawBase = typeof __BASE_PATH__ === "string" ? __BASE_PATH__ : "/";
    const prefix = rawBase.replace(/\/+$/, "");
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    const canonical = `${origin}${prefix}${path === "/" ? "/" : path}`;

    document.title = title;
    upsertMeta("name", "description", description);
    upsertMeta("name", "robots", robots);
    if (keywords) upsertMeta("name", "keywords", keywords);

    upsertMeta("property", "og:title", title);
    upsertMeta("property", "og:description", description);
    upsertMeta("property", "og:type", type);
    upsertMeta("property", "og:url", canonical);
    upsertMeta("property", "og:site_name", "看料大瓜");
    upsertMeta("property", "og:image", image);
    upsertMeta("property", "og:locale", "zh_CN");

    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", title);
    upsertMeta("name", "twitter:description", description);
    upsertMeta("name", "twitter:image", image);

    upsertCanonical(canonical);

    const existing = document.getElementById("page-jsonld");
    if (existing) existing.remove();
    if (jsonLdString) {
      const script = document.createElement("script");
      script.setAttribute("type", "application/ld+json");
      script.id = "page-jsonld";
      script.textContent = jsonLdString;
      document.head.appendChild(script);
    }
  }, [title, description, keywords, path, type, image, robots, jsonLdString]);
}